<template>
  <form @submit.prevent="saveForm()">
    <div class="modal-card" style="width: auto; height:auto">
      <header class="modal-card-head flex justify-between">
        <p class="modal-card-title">
          {{ resource ? `Edit: ${resource.name}` : 'Add Resource' }}
        </p>
      </header>
      <section class="modal-card-body">
        <d-field v-if="!resource" label="Select a Resource Type">
          <div class="flex">
            <d-radio v-model="resourceForm.type" native-value="File" class="mr-3">
              File
            </d-radio>
            <d-radio v-model="resourceForm.type" native-value="Link">
              Web URL
            </d-radio>
          </div>
        </d-field>

        <d-field v-if="resourceForm.type === 'File'" label="Click to Select File">
          <div>
            <input ref="fileUpload" type="file" style="display: none" @change="fileToUpload = $event.target.files[0]">
            <div class="flex items-center">
              <d-button size="sm" @click="$refs.fileUpload.click()">
                Browse
              </d-button>
              <span class="ml-3">{{ displayFileName }}</span>
            </div>
          </div>
        </d-field>

        <d-field v-if="resourceForm.type === 'Link'" label="Enter a Web URL">
          <d-input v-model="resourceForm.url" />
        </d-field>

        <d-field label="Name">
          <d-input v-model="resourceForm.name" required />
        </d-field>
        <d-field label="Description">
          <d-input v-model="resourceForm.description" type="textarea" required />
        </d-field>

        <d-field label="Resource Category">
          <d-select v-model="resourceForm.documentCategoryId" placeholder="Select one..." required expanded>
            <option v-for="category in resourceCategories" :key="category.id" :value="category.id">
              {{ category.name }}
            </option>
          </d-select>
        </d-field>

        <d-field v-if="hasRole('System Administrator')" label="Upload on Behalf Of">
          <div class="pt-2 relative mx-auto text-black">
            <d-autocomplete
              v-model="resourceForm.userId"
              :data="availableUsers"
              item-key="userId"
              icon="search"
              placeholder="Optionally select a user"
              field="fullName"
              position="top"
              @typing="getUsersDataAsync"
            >
              <template v-slot="{option}">
                <p>{{ option.fullName }}</p>
                <p class="text-sm text-gray-600">
                  {{ option.email }}
                </p>
              </template>
              <template slot="empty">
                No results found
              </template>
            </d-autocomplete>
          </div>
        </d-field>
        <permission-selector ref="permissionSelector" :key="entityId" :entity-id="entityId" entity-type="Document" />
      </section>
      <footer class="modal-card-foot justify-end">
        <d-btn flat @click="$parent.close()">
          Cancel
        </d-btn>
        <d-btn :loading="saving" type="submit">
          {{ resource ? 'Update Resource' : 'Add Resource' }}
        </d-btn>
      </footer>
    </div>
  </form>
</template>

<script>
  import { mapGetters } from 'vuex';
  import debounce from 'lodash/debounce';

  import axios from 'axios';
  export default {

    props: {
      userId: {
        type: String,
        default: () => '',
      },

      resource: {
        type: Object,
        default: null,
      },
    },

    data: () => ({
      resourceCategories: [],
      availableUsers: [],
      selectedCategory: '',
      selectedDocumentCategory: {},
      saving: false,
      entityId: 0,
      fileToUpload: '',
      resourceForm: {
        name: '',
        type: 'File',
        url: '',
        description: '',
        userId: '',
        documentCategoryId: 0,
      },
    }),

    computed: {
      displayFileName() {
        return this.fileToUpload.name ? this.fileToUpload.name : this.resourceForm.url.split('/').pop();
      },
      ...mapGetters('d2Auth', ['hasRole']),
    },

    watch: {
      'resourceForm.type': function (val) {
        if (val === 'File') this.resourceForm.url = '';
      },
    },

    mounted() {
      this.getResourceCategoriesAsync();
      if (this.resource) {
        // Set the form
        this.resourceForm = JSON.parse(JSON.stringify(this.resource));
        // Set the entityId for Entity Permissions
        this.entityId = this.resourceForm.id;
      }
    },

    methods: {
      async saveForm() {
        this.saving = true;

        if (this.resourceForm.type === 'File' && this.fileToUpload === '' && !this.resource) {
          this.$toast.error('You must select a resource to upload');
          this.saving = false;

          return;
        }
        if (this.resourceForm.type === 'Link' && this.resourceForm.url === '') {
          this.$toast.error('You must enter a link');
          this.saving = false;

          return;
        }

        try {
          if (this.resource) {
            // Update resource
            await axios.patch(`/api/resources/${this.resourceForm.id}`, this.resourceForm);
            await this.$refs.permissionSelector.saveData(this.resourceForm.id);
            // Update blob storage file
            await this.uploadResourceFile(this.resourceForm.id);
          } else {
            // Upload new resource
            const { data } = await axios.post('/api/resources', this.resourceForm);
            await this.$refs.permissionSelector.saveData(data.id);
            // Upload new resource file
            await this.uploadResourceFile(data.id);
          }

          this.$toast.success('Saved successfully!');
          await this.$parent.events.handleUpdate();
          this.$parent.close();
        } catch (error) {
          this.$toast.error(error.response.data[0]);
        } finally {
          this.saving = false;
        }
      },

      // upload to Azure Blob Storage
      async uploadResourceFile(id) {
        if (this.fileToUpload === '') {
          return;
        }
        const form = new FormData();
        form.append('File', this.fileToUpload);

        try {
          await axios.put(`/api/resources/${id}/upload`, form, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
        } catch (error) {
          this.$toast.error(error.message.data);
        }
      },

      async getResourceCategoriesAsync() {
        const { data } = await axios.get('/api/resource-category-lookup');
        this.resourceCategories = data;
      },

      getUsersDataAsync: debounce(async function (name = '') {
        this.isFetching = true;
        try {
          const { data } = await axios.get(`/api/user-lookup?filter=${name}`);
          this.availableUsers = data;
        } catch (error) {
          this.availableUsers = [];
          throw error;
        } finally {
          this.isFetching = false;
        }
      }, 500),
    },

  };
</script>